import { isWeaveUser } from '@frontend/auth-helpers';
import { getCookie, setCookie } from '@frontend/cookie';
import type { Channel, SiteChannels, SiteKey } from './types';

const SITE_NAME_COOKIE = 'siteName';
const WEAVE_CHANNEL_COOKIE = 'weaveChannel';
const WEAVE_PARENT_DOMAIN = '.getweave.com';
const ONE_YEAR_MS = 365 * 24 * 60 * 60 * 1000;

const CHANNELS = ['stable', 'edge', 'staging'] as const satisfies readonly Channel[];
const isChannel = (value: string | undefined): value is Channel =>
  (CHANNELS as readonly string[]).includes(value ?? '');

class SiteNameSync {
  /**
   * Assigns the per-site `siteName` cookie plus, when called from the main Weave app,
   * a `.getweave.com`-scoped `weaveChannel` cookie that other Weave subdomains can
   * inherit from on their next `init(...)` call. A valid global channel cookie also
   * allows reader sites to sync for users without a current Weave token.
   */
  init(siteKey: SiteKey, siteChannels: SiteChannels, channel: Channel = 'edge'): void {
    // Non-weave sites inherit the channel from the global cookie when the main Weave app has written it.
    const globalChannel = getCookie(WEAVE_CHANNEL_COOKIE);
    if (!isChannel(globalChannel) && !isWeaveUser()) return;

    const effectiveChannel = siteKey !== 'weave' && isChannel(globalChannel) ? globalChannel : channel;

    const targetHost = siteChannels[effectiveChannel];
    // Cookie domain is the site's own stable domain, so each site gets a uniquely-scoped siteName cookie.
    const siteCookieDomain = siteChannels.stable;
    const currentSiteName = getCookie(SITE_NAME_COOKIE);

    if (currentSiteName !== targetHost) {
      setCookie({
        name: SITE_NAME_COOKIE,
        value: targetHost,
        domain: siteCookieDomain,
        expires: new Date(Date.now() + ONE_YEAR_MS),
      });
    }

    // Only the main Weave app writes the global cookie that reader sites inherit above.
    if (siteKey === 'weave') {
      if (globalChannel !== channel) {
        setCookie({
          name: WEAVE_CHANNEL_COOKIE,
          value: channel,
          domain: WEAVE_PARENT_DOMAIN,
          expires: new Date(Date.now() + ONE_YEAR_MS),
        });
      }
    }
  }
}

export const siteNameSync = new SiteNameSync();
